import React, { forwardRef, useMemo, useEffect, useState, useRef } from 'react';
import { Button, Box, Alert } from '@amzn/awsui-components-console';
import {
	SupportedOperation,
	Status,
	OperationMetadata,
	GetOperationResponse,
} from '@amzn/snow-data-service-typescript-client';
import ShowSnowStatus from 'src/components/findEntity/ShowSNOWStatus';
import {
	COLLECTION_PREFERENCES,
	DEFAULT_PREFERENCES,
} from './OperationListConstants';
import {
	GenericTable,
	GenericTableColumn,
	GenericComponentData,
} from 'src/components/commonComponents/GenericTable';
import { useFetchForSDS } from 'src/hooks/useFetchForSDS';
import { SDS_APIGATEWAY_ENDPOINT } from 'src/config';
import { useNavigation } from '../../navigation/hooks/useNavigation';
import { ROUTES } from '../../navigation/types/navigationTypes';
import { useStore } from 'src/stores/store';
import useFetchOnUserActionUsingSDS from 'src/hooks/useFetchOnUserActionUsingSDS';
import DeleteConfirmationModal from 'src/components/commonComponents/DeleteConfirmationModal/DeleteConfirmationModal';
import { callSNOWDataService } from 'src/utils/snowDataServiceWrapper';

type OperationProfile = SupportedOperation & Partial<OperationMetadata> & GenericComponentData;

interface OperationListProps {
	selectedApplicationId: string;
}

const OperationList = forwardRef<{ fetchData: () => void }, OperationListProps>(
	({ selectedApplicationId }, ref) => {
		const { navigate } = useNavigation();
		const {
			setSelectedOperation,
			selectedOperation,
			addNavigationError,
		} = useStore();

		const [deleteModalVisible, setDeleteModalVisible] = useState(false);
		const [operationToDelete, setOperationToDelete] = useState<string | null>(
			null
		);

		const { isLoading, data, error, fetchData } = useFetchForSDS({
			url: SDS_APIGATEWAY_ENDPOINT,
			path: '/operation/listOperations',
			queryParams: {
				status: Status.ACTIVE,
				count: 300,
			},
			method: 'GET',
		});

		const {
			result: { isLoading: isDeleting, error: deleteError },
			onClickCB: deleteOperation,
		} = useFetchOnUserActionUsingSDS<void>();

		useEffect(() => {
			if (error) {
				addNavigationError({
					message: `Failed to load operations: ${error}`,
					type: 'error',
					timestamp: Date.now(),
				});
			}

			if (deleteError) {
				addNavigationError({
					message: `Failed to delete operation: ${deleteError}`,
					type: 'error',
					timestamp: Date.now(),
				});
			}
		}, [error, deleteError, addNavigationError]);


		const items = useMemo(() => {
			if (!data) return [];

			const allOperations =
				(data as { operations?: SupportedOperation[] })?.operations || [];

			const filtered = allOperations
				.filter((op) => op.applicationId === selectedApplicationId)
				.map((op) => ({
					...op,
					id: op.operationId,
				})) as OperationProfile[];

			return filtered;
		}, [data, selectedApplicationId]);

		const [metadataMap, setMetadataMap] = useState<Record<string, OperationMetadata>>({});
		const fetchedIdsRef = useRef<Set<string>>(new Set());

		useEffect(() => {
			const idsToFetch = items
				.map((op) => op.operationId)
				.filter((id): id is string => !!id && !fetchedIdsRef.current.has(id));

			if (idsToFetch.length === 0) return;

			idsToFetch.forEach((id) => fetchedIdsRef.current.add(id));

			Promise.all(
				idsToFetch.map((id) =>
					callSNOWDataService({
						path: `/operation/${id}`,
						method: 'GET',
						url: SDS_APIGATEWAY_ENDPOINT,
						body: undefined,
					})
						.then((res: GetOperationResponse) => ({ id, metadata: res.operationMetadata }))
						.catch(() => ({ id, metadata: undefined }))
				)
			).then((results) => {
				const newEntries: Record<string, OperationMetadata> = {};
				results.forEach(({ id, metadata }) => {
					if (metadata) {
						newEntries[id] = metadata;
					}
				});
				if (Object.keys(newEntries).length > 0) {
					setMetadataMap((prev) => ({ ...prev, ...newEntries }));
				}
			});
		}, [items]);

		const enrichedItems = useMemo(
			() =>
				items.map((op) => ({
					...op,
					...(op.operationId && metadataMap[op.operationId]
						? metadataMap[op.operationId]
						: {}),
					id: op.operationId,
				})) as OperationProfile[],
			[items, metadataMap]
		);

		const handleViewDetails = (operationId: string) => {
			const operation = enrichedItems.find((op) => op.operationId === operationId);

			if (operation) {
				setSelectedOperation(operation);
				navigate(
					ROUTES.OPERATION_DETAILS,
					`/applicationManagement/${selectedApplicationId}/${operationId}`
				);
			}
		};

		const handleExportToTask = (operationId: string) => {
			const operation = enrichedItems.find((op) => op.operationId === operationId);

			if (operation) {
				setSelectedOperation(operation);
				navigate(
					ROUTES.EXPORT_OPERATION_TO_TASK,
					`/applicationManagement/${selectedApplicationId}/${operationId}/export`
				);
			}
		};

		const handleAddOperation = () => {
			navigate(
				ROUTES.ADD_OPERATION,
				`/applicationManagement/${selectedApplicationId}/add`
			);
		};

		const handleEditOperation = (operationId: string) => {
			const operation = enrichedItems.find((op) => op.operationId === operationId);

			if (operation) {
				setSelectedOperation(operation);
				navigate(
					ROUTES.EDIT_OPERATION,
					`/applicationManagement/${selectedApplicationId}/${operationId}/edit`
				);
			}
		};

		const handleDeleteOperation = (operationId: string) => {
			setOperationToDelete(operationId);
			setDeleteModalVisible(true);
		};

		const confirmDelete = async () => {
			if (!operationToDelete) return;

			await deleteOperation({
				path: `/operation/${operationToDelete}`,
				method: 'DELETE',
				url: SDS_APIGATEWAY_ENDPOINT,
			});

			
			setDeleteModalVisible(false);
			setOperationToDelete(null);
			fetchData();
		};

		const cancelDelete = () => {
			setDeleteModalVisible(false);
			setOperationToDelete(null);
		};

		const columns: GenericTableColumn<OperationProfile>[] = [
			{
				id: 'operationName',
				header: 'Operation Name',
				cell: (item: OperationProfile) => (
					<Button
						onClick={() => handleViewDetails(item.operationId!)}
						variant="inline-link"
					>
						{item.operationName}
					</Button>
				),
				sortingField: 'operationName',
				minWidth: 150,
			},
			{
				id: 'version',
				header: 'Version',
				cell: (item: OperationProfile) => item.version,
				sortingField: 'version',
			},
			{
				id: 'description',
				header: 'Description',
				cell: (item: OperationProfile) => item.description,
			},
			{
				id: 'status',
				header: 'Status',
				cell: (item: OperationProfile) => (
					<ShowSnowStatus status={item.status} />
				),
				sortingField: 'status',
			},
			{
				id: 'integrationType',
				header: 'Integration Type',
				cell: (item: OperationProfile) => String(item.integrationType || '-'),
				sortingField: 'integrationType',
			},
			{
				id: 'idempotent',
				header: 'Idempotent',
				cell: (item: OperationProfile) => (item.idempotent ? 'Yes' : 'No'),
				sortingField: 'idempotent',
			},
			{
				id: 'createdBy',
				header: 'Created By',
				cell: (item: OperationProfile) => String(item.createdBy || '-'),
				sortingField: 'createdBy',
			},
			{
				id: 'lastUpdatedBy',
				header: 'Last Updated By',
				cell: (item: OperationProfile) => String(item.lastUpdatedBy || '-'),
				sortingField: 'lastUpdatedBy',
			},
		];

		if (!selectedApplicationId) {
			return (
				<Box padding="l">
					<Alert type="info" data-testid="select-application-alert">
						Please select an application first to view its operations.
					</Alert>
				</Box>
			);
		}

		const dropdownActions = [
			{
				text: 'View Details',
				id: 'view-details',
				handler: handleViewDetails,
			},
			{
				text: 'Edit Operation',
				id: 'edit-operation',
				handler: handleEditOperation,
			},
			{
				text: 'Export to Task',
				id: 'export-to-task',
				handler: handleExportToTask,
			},
			{
				text: 'Delete Operation',
				id: 'delete-operation',
				handler: handleDeleteOperation,
			},
		];

		const operationName = enrichedItems.find(
			(item) => item.operationId === operationToDelete
		)?.operationName;

		return (
			<>
				<GenericTable<OperationProfile>
					ref={ref}
					title={`Operations (${enrichedItems.length})`}
					items={enrichedItems}
					columns={columns}
					isLoading={isLoading}
					error={error}
					selectedItemId={selectedOperation?.operationId || null}
					onSelectionChange={(id) => {
						const op = enrichedItems.find((item) => item.operationId === id);

						if (op) {
							setSelectedOperation(op);
						} else {
							setSelectedOperation(null);
						}
					}}
					onRefresh={fetchData}
					primaryAction={{
						text: 'Add Operation',
						onClick: handleAddOperation,
					}}
					dropdownActions={dropdownActions}
					filterPlaceholder="Find operations"
					filterFields={['operationName', 'description', 'applicationName']}
					emptyTitle="No operations found"
					emptyDescription={error ?? ''}
					emptyAction={{
						text: 'Add Operation',
						onClick: handleAddOperation,
						variant: 'primary',
					}}
					collectionPreferences={COLLECTION_PREFERENCES}
					defaultPreferences={DEFAULT_PREFERENCES}
					testIdPrefix="operation-list"
				/>
				<DeleteConfirmationModal
					visible={deleteModalVisible}
					onDismiss={cancelDelete}
					onConfirm={confirmDelete}
					isDeleting={isDeleting}
					itemName={operationName || ''}
					itemType="operation"
					testIdPrefix="delete-operation"
				/>
			</>
		);
	}
);

OperationList.displayName = 'OperationList';

export default OperationList;
